import { useEffect, useRef } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useVoiceStore } from "../../../stores/voiceStore.js";

// ── Video tile ────────────────────────────────────────────────────────────────
function VideoTile({ participant, stream, screenStream, isLocal, size }) {
  const videoRef  = useRef(null);
  const screenRef = useRef(null);

  useEffect(() => { if (videoRef.current  && stream)       videoRef.current.srcObject  = stream; },       [stream]);
  useEffect(() => { if (screenRef.current && screenStream) screenRef.current.srcObject = screenStream; }, [screenStream]);

  const name    = participant.name || 'Unknown';
  const initial = name[0]?.toUpperCase() || '?';
  const large   = size === 'large';

  return (
    <div className={`voice-tile${participant.isSpeaking ? ' speaking' : ''}`}
      style={{ position: 'relative', background: '#111827', borderRadius: 10, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: large ? 360 : 180 }}
    >
      {screenStream ? (
        <video ref={screenRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : participant.isCameraOn && stream ? (
        <video ref={videoRef} autoPlay playsInline muted={isLocal}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transform: isLocal ? 'scaleX(-1)' : 'none' }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <div style={{ width: large ? 80 : 52, height: large ? 80 : 52, borderRadius: '50%', background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: large ? 32 : 20, fontWeight: 800, color: '#fff' }}>
            {participant.avatar || initial}
          </div>
          {large && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{name}{isLocal ? ' (You)' : ''}</div>}
        </div>
      )}

      {/* Name + status bar */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px 10px 8px', background: 'linear-gradient(transparent,rgba(0,0,0,0.75))', display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}{isLocal ? ' (You)' : ''}
        </span>
        <span style={{ fontSize: 13, opacity: participant.isMuted ? 1 : 0.5 }}>{participant.isMuted ? '🔇' : '🎤'}</span>
        {participant.isCameraOn && <span style={{ fontSize: 12 }}>📷</span>}
      </div>

      {participant.isSpeaking && (
        <div style={{ position: 'absolute', inset: 0, borderRadius: 10, border: '2px solid #22c55e', pointerEvents: 'none' }} />
      )}
    </div>
  );
}

// ── Control button ────────────────────────────────────────────────────────────
function CtrlBtn({ icon, iconOff, label, active, danger, onClick }) {
  return (
    <button onClick={onClick} title={label} className="voice-ctrl-btn" style={{
      background: active ? 'rgba(255,255,255,0.14)' : danger ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.07)',
      border: `1px solid ${active ? 'rgba(255,255,255,0.28)' : danger ? 'rgba(239,68,68,0.45)' : 'rgba(255,255,255,0.1)'}`,
      color: danger ? '#ef4444' : '#fff',
    }}>
      <span style={{ fontSize: 22 }}>{active && iconOff ? iconOff : icon}</span>
      <span style={{ fontSize: 10, opacity: 0.65, whiteSpace: 'nowrap', marginTop: 3 }}>{label}</span>
    </button>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function VoiceCallModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const {
    channelName, isMuted, isDeafened, isCameraOn, isSharingScreen,
    localStream, screenStream, participants,
    toggleMute, toggleDeafen, toggleCamera, toggleScreenShare, leave,
  } = useVoiceStore();

  if (!isOpen || type !== 'voiceCall') return null;

  const displayName = data?.channelName || channelName || 'Voice Channel';
  const localP      = participants.find(p => p.isLocal) || { name: 'You', isMuted, isCameraOn };
  const others      = participants.filter(p => !p.isLocal);
  const count       = participants.length;
  const hasScreen   = isSharingScreen && screenStream;

  const handleLeave = () => { leave(); onClose(); };

  const gridCols = hasScreen ? '1fr'
    : count <= 1 ? '1fr'
    : count <= 4 ? 'repeat(2, 1fr)'
    : 'repeat(3, 1fr)';

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0d0d14', display: 'flex', flexDirection: 'column', zIndex: 1000 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(var(--aethex-primary),0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🔊</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{displayName}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{count} {count === 1 ? 'participant' : 'participants'}</div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 18, cursor: 'pointer', padding: 4, lineHeight: 1 }}>✕</button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: 16, overflow: 'hidden', display: 'flex', gap: 12 }}>
        {/* Screen share main view */}
        {hasScreen && (
          <div style={{ flex: '0 0 68%' }}>
            <VideoTile participant={{ ...localP, isCameraOn: false }} stream={null} screenStream={screenStream} isLocal size="large" />
          </div>
        )}

        {/* Participant tiles */}
        <div style={{ flex: 1, display: 'grid', gap: 10, gridTemplateColumns: gridCols, alignContent: 'start', overflowY: 'auto' }}>
          <VideoTile
            participant={{ ...localP, isMuted, isCameraOn }}
            stream={localStream} screenStream={null}
            isLocal size={count === 1 && !hasScreen ? 'large' : 'normal'}
          />
          {others.map(p => (
            <VideoTile key={p.id} participant={p} stream={p.stream || null} screenStream={null} isLocal={false} />
          ))}
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px 22px 22px', borderTop: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
        <CtrlBtn icon="🎤" iconOff="🔇" label={isMuted        ? 'Unmute'    : 'Mute'}         active={isMuted}         onClick={toggleMute} />
        <CtrlBtn icon="🔊" iconOff="🔕" label={isDeafened     ? 'Undeafen'  : 'Deafen'}       active={isDeafened}      onClick={toggleDeafen} />
        <CtrlBtn icon="📷" iconOff="📵" label={isCameraOn     ? 'Cam Off'   : 'Cam On'}       active={isCameraOn}      onClick={toggleCamera} />
        <CtrlBtn icon="🖥️"              label={isSharingScreen ? 'Stop Share': 'Share Screen'} active={isSharingScreen} onClick={toggleScreenShare} />
        <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
        <CtrlBtn icon="📞" label="Leave" danger onClick={handleLeave} />
      </div>
    </div>
  );
}
